import { cn } from '@/lib/utils';
import { CodigoBarras } from '@/componentes/etiqueta/CodigoBarras';

export interface EtiquetaSeparacao {
  etiqueta: number;
  codigoBarras: string | null;
  emissao: number;
  televenda: number;
  centroEstoque: number;
  sequencia: number;
  volumes: string | null;
  volumeProdutos: string | null;
  volumePedido: string | null;
  produto: number;
  descricao: string | null;
  descricaoEtiqueta: string | null;
  quantidade: number;
  localizador: number | null;
  endereco: string | null;
  corredor: string | null;
  status: string | null;
  lote: string | null;
  estado: string | null;
  transporte: string | null;
  localFaturamento: string | null;
  presente: boolean;
  pedidoInternet: string | null;
  pedidoMarketplace: string | null;
}

/**
 * A etiqueta de separação, 100 × 50 mm.
 *
 * O layout NÃO foi desenhado aqui: foi MEDIDO no PDF que o Procfit gera.
 * Extraí as coordenadas e os corpos de letra do arquivo de referência da
 * emissão 268065 e reproduzi posição por posição — por isso as medidas estão
 * em PONTOS e a origem é o canto inferior esquerdo (`bottom`/`left`), que é o
 * sistema do PDF. Converter para milímetros e "arredondar bonito" seria
 * introduzir diferença onde o objetivo é não ter nenhuma: o separador conhece
 * este papel de cor e lê pela posição, não pelo rótulo.
 *
 * O mapa do original, em pt a partir do canto inferior esquerdo:
 *
 *   FAT - 2201        (9.8, 130.3)  9pt      título centrado (72.8, 130.6)
 *   nº da emissão     (234.3, 130.3) 9pt
 *   "Televenda:"      (8.1, 113.3)  9pt
 *   nº da televenda   (9.0, 92.9)   ~14pt    código de barras (90.8, 90.0)
 *                                            179.5 × 27.7 pt
 *   descrição         (6.0, 70.3)   9pt, duas linhas
 *   transporte        (6.0, 42.0)   9pt      UF (206.5, 45.0) 8pt
 *                                            vol. produto (244.7, 43.4)
 *                                            vol. pedido  (244.7, 26.1)
 *   caixa localizador (6.0, 16.5)   202.5 × 19.5 pt, texto ~14pt
 *   data/hora         (210.8, 15.7) 6pt
 */
export function EtiquetaPapel({
  etiqueta,
  impressoEm,
  previa = false,
  naoEmitida = false,
}: {
  etiqueta: EtiquetaSeparacao;
  /** Carimbo de impressão — o `DATA_IMPRESSAO` do relatório. */
  impressoEm: string;
  /** Na tela ganha moldura; no papel, não. */
  previa?: boolean;
  /**
   * Etiqueta que AINDA NÃO existe no ERP.
   *
   * Sai sem código de barras e marcada: o código é derivado do id que só
   * nasce na emissão, e um código inventado seria pior que nenhum — a esteira
   * bipa, não encontra, e a peça vira divergência.
   */
  naoEmitida?: boolean;
}): JSX.Element {
  const semEndereco = !etiqueta.endereco || etiqueta.endereco === 'ND';

  return (
    <article
      className={cn(
        'etiqueta relative overflow-hidden bg-white font-sans text-black',
        previa && 'shadow-cartao ring-1 ring-borda',
      )}
      style={{ width: '283.46pt', height: '141.73pt', boxSizing: 'border-box' }}
    >
      <span style={{ position: 'absolute', left: '9.81pt', bottom: '130.26pt', fontSize: '9pt' }}>
        {etiqueta.localFaturamento ?? ''}
      </span>
      <span
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '130.61pt',
          fontSize: '9pt',
          textAlign: 'center',
        }}
      >
        Etiqueta de Separação de Pedidos
      </span>
      <span
        style={{
          position: 'absolute',
          left: '234.33pt',
          bottom: '130.26pt',
          fontSize: '9pt',
          fontFamily: 'monospace',
        }}
      >
        {etiqueta.emissao}
      </span>

      <span style={{ position: 'absolute', left: '8.07pt', bottom: '113.32pt', fontSize: '9pt' }}>
        Televenda:
      </span>
      <span
        style={{
          position: 'absolute',
          left: '9pt',
          bottom: '92.88pt',
          fontSize: '15pt',
          fontWeight: 700,
          fontFamily: 'monospace',
          letterSpacing: '0.02em',
        }}
      >
        {etiqueta.televenda}
      </span>

      {/* O código de barras: 179,5 × 27,7 pt a partir de (90.8, 90.0). */}
      <span style={{ position: 'absolute', left: '90.75pt', bottom: '90pt', lineHeight: 0 }}>
        {naoEmitida ? (
          <span
            style={{
              display: 'block',
              width: '179.5pt',
              height: '27.7pt',
              border: '1pt dashed #000',
              fontSize: '7pt',
              textAlign: 'center',
              lineHeight: '27.7pt',
            }}
          >
            código de barras sai na emissão
          </span>
        ) : (
          <CodigoBarras texto={etiqueta.codigoBarras ?? ''} larguraPt={179.5} alturaPt={27.7} />
        )}
      </span>

      {/*
        A descrição usa a LARGURA INTEIRA da etiqueta, e não os 200 pt do
        original.

        A faixa entre o código de barras (que termina em 90 pt) e a linha do
        transporte (que começa em 42 pt) está livre de ponta a ponta — nada do
        layout ocupa a direita nessa altura. Com 200 pt, título comprido
        quebrava cedo e a segunda linha estourava a caixa: o Fogão Consul
        CFO4NAB é o caso, 110 caracteres mais o EAN. Com 271 pt cabem as duas
        linhas sem cortar, e nada se aproxima do barcode (sobram 6 pt).
      */}
      <span
        style={{
          position: 'absolute',
          left: '6pt',
          bottom: '58pt',
          width: '271pt',
          height: '26pt',
          fontSize: '9pt',
          lineHeight: '11pt',
          overflow: 'hidden',
          wordBreak: 'normal',
          hyphens: 'none',
        }}
      >
        {etiqueta.descricaoEtiqueta ?? `${etiqueta.produto} - ${etiqueta.descricao ?? ''}`}
      </span>

      <span style={{ position: 'absolute', left: '6pt', bottom: '41.99pt', fontSize: '9pt' }}>
        {etiqueta.transporte ?? ''}
      </span>
      <span style={{ position: 'absolute', left: '206.54pt', bottom: '45.04pt', fontSize: '8pt' }}>
        {etiqueta.estado ?? ''}
      </span>
      <span
        style={{
          position: 'absolute',
          left: '244.74pt',
          bottom: '43.38pt',
          fontSize: '9pt',
          fontFamily: 'monospace',
        }}
      >
        {etiqueta.volumeProdutos ?? ''}
      </span>
      <span
        style={{
          position: 'absolute',
          left: '244.74pt',
          bottom: '26.13pt',
          fontSize: '9pt',
          fontFamily: 'monospace',
        }}
      >
        {etiqueta.volumePedido ?? etiqueta.volumes ?? ''}
      </span>

      {/* A caixa do localizador: é o maior elemento, e é para onde se anda. */}
      <span
        style={{
          position: 'absolute',
          left: '6pt',
          bottom: '16.48pt',
          width: '202.5pt',
          height: '19.5pt',
          border: '1pt solid #000',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14pt',
          fontWeight: 700,
          fontFamily: 'monospace',
          fontStyle: semEndereco ? 'italic' : 'normal',
        }}
      >
        {etiqueta.endereco ?? 'ND'}
      </span>

      <span style={{ position: 'absolute', left: '210.84pt', bottom: '15.66pt', fontSize: '6pt' }}>
        {impressoEm}
      </span>

      {etiqueta.presente ? (
        <span
          style={{
            position: 'absolute',
            left: '212pt',
            bottom: '60pt',
            fontSize: '8pt',
            fontWeight: 700,
            border: '1pt solid #000',
            padding: '0 2pt',
          }}
        >
          PRESENTE
        </span>
      ) : null}

      {naoEmitida ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: '6pt',
            bottom: '2pt',
            fontSize: '6pt',
            fontWeight: 700,
            letterSpacing: '0.1em',
          }}
        >
          PRÉVIA — ONDA AINDA NÃO EMITIDA
        </span>
      ) : null}
    </article>
  );
}
